import React, { useCallback, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import { Form, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  selectCrossClientLandingCurrentExecution,
  selectCrossClientInitLoading,
  setCrossClientLandingExecution,
  setCrossClientEvaluateLoading,
  setCrossClientInitLoading,
  selectCrossClientEvaluateLoading,
  selectCrossClientLandingCompletedExecutionsLoading,
  selectCrossClientLandingRunningExecutionsLoading,
  selectCrossClientLandingRunningExecutionCount,
} from '../../../store/crossClient/crossClientLanding'
import { ReactComponent as ZocksLogo } from '../../../assets/ZocksLogo.svg'
import { useEvaluateCrossClientQueryMutation, useInitializeCrossClientQueryMutation } from '../../../store/crossClient/crossClientExecution'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { CrossClientQueryLogicalQuestionDecomposition, CrossClientQueryPhases } from '../../../models/CrossClient'
import { Button } from '../../../components/Button/Button'
import { useNavigate } from 'react-router-dom'
import { CrossClientClauses } from '../Clauses/CrossClientClauses'
import { CrossClientLandingFooter } from './Footer/CrossClientLandingFooter'
import { CrossClientLandingInitialization } from './CrossClientLandingInitialization'
import { CrossClientLimit } from './Limit/CrossClientLimit'
import { useCrossClientRemainingQueries } from './Limit/hooks'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { CrossClientDefault } from '../Defaults/crossClientDefaults'
import './CrossClientLanding.scss'

const MAX_USER_QUESTION_LENGTH = 350
const MAX_CONCURRENT_EXECUTIONS = 3

export const CrossClientLanding = () => {
  const navigate = useNavigate()

  const { tracked, trackingClient } = useTracking()

  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const initLoading = useAppSelector(selectCrossClientInitLoading)
  const currentExecution = useAppSelector(selectCrossClientLandingCurrentExecution)
  const evaluateLoading = useAppSelector(selectCrossClientEvaluateLoading)
  const completedExecutionsLoading = useAppSelector(selectCrossClientLandingCompletedExecutionsLoading)
  const runningExecutionsLoading = useAppSelector(selectCrossClientLandingRunningExecutionsLoading)
  const runningExecutionCount = useAppSelector(selectCrossClientLandingRunningExecutionCount)

  const { remainingCcQueriesPerWeek } = useCrossClientRemainingQueries()

  const [userQuestion, setUserQuestion] = useState<string>('')
  const [selectedDefault, setSelectedDefault] = useState<CrossClientDefault | null>(null)
  const [inputHovered, setInputHovered] = useState<boolean>(false)
  const [showFooter, setShowFooter] = useState<boolean>(true)

  const [triggerInitializeCrossClientQuery] = useInitializeCrossClientQueryMutation()
  const [triggerEvaluateCrossClientQuery] = useEvaluateCrossClientQueryMutation()

  const atomicClauses = selectedDefault ? selectedDefault.decomposition.atomicClauses : (currentExecution?.decomposition?.atomicClauses ?? [])
  const numberOfClauses = atomicClauses.length
  const tooManyConcurrentExecutions = runningExecutionCount >= MAX_CONCURRENT_EXECUTIONS
  const cannotExecuteNew = !remainingCcQueriesPerWeek || tooManyConcurrentExecutions
  const buttonDisabled = cannotExecuteNew || userQuestion.trim().length < 3 || userQuestion.trim().length > MAX_USER_QUESTION_LENGTH

  const initializeCrossClientExecution = useCallback(
    async (userQuestion: string, decomposition?: CrossClientQueryLogicalQuestionDecomposition) => {
      try {
        dispatch(setCrossClientLandingExecution(null))
        dispatch(setCrossClientInitLoading(true))
        const execution = await triggerInitializeCrossClientQuery({ accountId, payload: { ownerId: currentUserId, userQuestion, decomposition } }).unwrap()
        dispatch(setCrossClientLandingExecution(execution))
        return execution
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setCrossClientInitLoading(false))
      }
    },
    [dispatch, accountId, currentUserId, triggerInitializeCrossClientQuery],
  )

  const evaluateCrossClientExecution = useCallback(
    async (executionId: string) => {
      try {
        dispatch(setCrossClientEvaluateLoading(true))
        await triggerEvaluateCrossClientQuery({ accountId, executionId }).unwrap()
        navigate(`/cross-client/executions/${executionId}`, { replace: true })
      } catch (e) {
        console.error(e)
        dispatch(setCrossClientEvaluateLoading(false))
      }
    },
    [dispatch, navigate, accountId, triggerEvaluateCrossClientQuery],
  )

  const initError = useMemo(() => {
    if (!currentExecution) {
      return null
    }
    const { phase } = currentExecution
    if (
      phase === CrossClientQueryPhases.NO_CLIENTS_WERE_FOUND ||
      phase === CrossClientQueryPhases.NO_CLIENTS_AFTER_FILTER_IDS ||
      phase === CrossClientQueryPhases.NO_CLIENTS_AFTER_FILTER_ROLE
    ) {
      return 'No clients found'
    }
    if (currentExecution.phase === CrossClientQueryPhases.QUESTION_ERROR) {
      return 'Unable to decompose question'
    }
    return null
  }, [currentExecution])

  const handleConfirm = tracked(
    async () => {
      if (selectedDefault) {
        const execution = await initializeCrossClientExecution(selectedDefault.description, selectedDefault.decomposition)
        if (!execution) {
          return
        }
        await evaluateCrossClientExecution(execution.executionId)
      }
      if (!currentExecution) {
        return
      }
      await evaluateCrossClientExecution(currentExecution.executionId)
    },
    {
      eventName: TrackEvent.X_CLIENT_QUERY_CONFIRMED,
      eventDetails: { type: selectedDefault ? 'pre-defined' : 'custom', query: selectedDefault ? selectedDefault.description : userQuestion },
    },
  )

  const handleRestart = tracked(
    () => {
      dispatch(setCrossClientLandingExecution(null))
      setUserQuestion('')
      setSelectedDefault(null)
      setShowFooter(true)
    },
    {
      eventName: TrackEvent.X_CLIENT_QUERY_DENIED,
      eventDetails: { type: selectedDefault ? 'pre-defined' : 'custom', query: selectedDefault ? selectedDefault.description : userQuestion },
    },
  )

  const handleInitializeCustomCrossClientQuery = tracked(
    async () => {
      await initializeCrossClientExecution(userQuestion)
    },
    {
      eventName: TrackEvent.X_CLIENT_QUERY_SUBMITTED,
      eventDetails: {
        type: 'custom',
        query: userQuestion,
      },
    },
  )

  const handleDefaultCrossClientQuerySelected = tracked(
    async (selectedDefault: CrossClientDefault) => {
      setSelectedDefault(selectedDefault)
    },
    {
      eventName: TrackEvent.X_CLIENT_QUERY_SUBMITTED,
      eventDetails: {
        type: 'pre-defined',
      },
    },
    {
      eventDetailsExtractor: (selectedDefault: CrossClientDefault) => {
        return { query: selectedDefault.description }
      },
    },
  )

  useEffect(() => {
    trackingClient.track({
      eventName: TrackEvent.X_CLIENT_OPENED,
    })
  }, [trackingClient])

  return (
    <>
      <CrossClientLandingInitialization />
      <div className='truly-full-size-content d-flex flex-column align-items-center zocks-gap-4 cross-client-landing-container'>
        <div className='d-flex flex-column gap-3 align-items-center'>
          <div className='paragraph-small zocks-mt-4 py-1 zocks-px-0.75 text-uppercase zocks-border-1.0 medium zocks-text-gray-700 cross-client-landing-header'>
            Client Queries
          </div>
          <div className='d-flex flex-column gap-2 text-center'>
            <h2 className='medium'>Find the right clients for every opportunity</h2>
            <div className='paragraph-medium'>Ask questions in plain language. Your AI Assistant delivers results in seconds.</div>
            <CrossClientLimit />
          </div>
        </div>
        <div className='d-flex flex-column w-100 align-items-center'>
          <div
            className={classNames('d-flex flex-column w-100 p-4 gap-3 cross-client-input cross-client-transition zocks-border-1.25 border border-1 ', {
              'bg-white': !cannotExecuteNew || initLoading || !!currentExecution,
              'zocks-bg-color-gray-100': cannotExecuteNew,
              'zocks-border-color-gray-200 cross-client-input-initial-shadow': !userQuestion.trim() && !currentExecution && !inputHovered,
              'cross-client-input-hover cross-client-input-initial-shadow': !userQuestion.trim() && !currentExecution && inputHovered,
              'zocks-border-color-gray-200 cross-client-input-decomp-shadow': !!userQuestion.trim() && currentExecution,
              'zocks-border-color-primary cross-client-input-purple-shadow': !!userQuestion.trim() && !currentExecution,
            })}
            onMouseEnter={() => setInputHovered(true)}
            onMouseLeave={() => setInputHovered(false)}
          >
            {!selectedDefault ? (
              <div className='d-flex gap-3'>
                <Form.Control
                  className={classNames('p-0 border-0 cross-client-input-textarea', { 'zocks-bg-color-gray-100': cannotExecuteNew })}
                  as='textarea'
                  value={userQuestion}
                  maxLength={MAX_USER_QUESTION_LENGTH}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                    setUserQuestion(e.currentTarget.value)
                  }}
                  disabled={cannotExecuteNew || initLoading || !!currentExecution}
                  onKeyDown={async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                    if (initLoading || !!currentExecution) {
                      return
                    }
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      if (e.ctrlKey || e.metaKey) {
                        setUserQuestion((chatMessage) => `${chatMessage}\n`)
                      } else if (!buttonDisabled) {
                        await handleInitializeCustomCrossClientQuery()
                      }
                    }
                  }}
                  placeholder='Example: Which clients are approaching retirement with over $500k in assets?'
                />

                {!initLoading && !currentExecution ? (
                  <OverlayTrigger
                    trigger={tooManyConcurrentExecutions ? ['hover', 'focus'] : []}
                    overlay={
                      <Tooltip id='evaluate-tooltip' className='position-fixed'>
                        Maximum concurrent queries running. Please wait for one to finish before submitting.
                      </Tooltip>
                    }
                  >
                    <Button
                      variant='borderless'
                      className={classNames('button-medium px-0 align-items-end cross-client-landing-button', { 'zocks-bg-color-gray-100': cannotExecuteNew })}
                      onClick={handleInitializeCustomCrossClientQuery}
                      disabled={buttonDisabled}
                    >
                      <i className='bi bi-arrow-right-circle-fill fs-2' />
                    </Button>
                  </OverlayTrigger>
                ) : null}
              </div>
            ) : null}
            {initLoading && !selectedDefault ? (
              <div className='d-flex zocks-gap-0.75 align-items-center'>
                <div className='d-flex align-items-center justify-content-center bg-black zocks-border-0.25 cross-client-loader-logo'>
                  <ZocksLogo width={12} height={12} />
                </div>
                <div className='paragraph-medium shimmering-loader-text'>Breaking down your question into search criteria…</div>
              </div>
            ) : null}
            {initError ? (
              <div className='d-flex flex-column'>
                <div className='d-flex zocks-gap-0.75 align-items-start'>
                  <div className='d-flex align-items-center justify-content-center bg-black zocks-border-0.25 cross-client-loader-logo'>
                    <ZocksLogo width={12} height={12} />
                  </div>
                  <div className='d-flex flex-column gap-3 align-items-start'>
                    <div className='paragraph-medium'>I couldn't identify specific criteria from your question.</div>
                    <div className='paragraph-medium'>Please add more details so I can search your clients effectively.</div>
                  </div>
                </div>
                <div className='d-flex gap-2 align-items-center justify-content-end'>
                  <Button variant='header' className='button-medium' onClick={handleRestart}>
                    Ask another question
                  </Button>
                </div>
              </div>
            ) : numberOfClauses ? (
              <div className='d-flex flex-column'>
                <div className='d-flex zocks-gap-0.75 align-items-start'>
                  <div className='d-flex align-items-center justify-content-center bg-black zocks-border-0.25 cross-client-loader-logo'>
                    <ZocksLogo width={12} height={12} />
                  </div>
                  <div className='d-flex flex-column gap-3 align-items-start'>
                    <div className='paragraph-medium'>
                      I'll check all clients against {numberOfClauses === 1 ? 'this criterion' : `these ${numberOfClauses} criteria`}:
                    </div>
                    <CrossClientClauses atomicClauses={atomicClauses} />
                    <div className='paragraph-medium'>Does this look right?</div>
                  </div>
                </div>
                <div className='d-flex gap-2 align-items-center justify-content-end'>
                  <Button variant='header' className='button-medium' onClick={handleRestart} disabled={initLoading || evaluateLoading}>
                    Ask another question
                  </Button>
                  <Button variant='primary' className='button-medium' onClick={handleConfirm} loading={initLoading || evaluateLoading}>
                    Looks good
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
        {showFooter ? (
          <div
            className={classNames('d-flex flex-column pt-4 align-items-center zocks-gap-2 w-100 fade', {
              'opacity-0': initLoading || currentExecution || selectedDefault,
              'opacity-100': !initLoading && !currentExecution && !selectedDefault,
            })}
            onTransitionEnd={() => setShowFooter(!initLoading && !currentExecution && !selectedDefault)}
          >
            {!completedExecutionsLoading && !runningExecutionsLoading ? (
              <CrossClientLandingFooter onDefaultSelected={handleDefaultCrossClientQuerySelected} />
            ) : null}
          </div>
        ) : null}
      </div>
    </>
  )
}
